<template>
  <aside
    v-if="eligible && !failed"
    :class="['adsense-container', containerClass]"
    :aria-label="$t('ads.advertisement')"
    :data-ad-placement="placement"
  >
    <div class="ad-caption tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
      <span>{{ $t('ads.advertisement') }}</span>
      <router-link
        :to="{ path: '/upgrade', query: { from: `ads_${placement}` } }"
        data-google-vignette="false"
        @click="track('ad_pro_click')"
      >
        {{ $t('ads.pro-link') }} ↗
      </router-link>
    </div>
    <div
      v-if="preview"
      class="ad-preview"
      :style="adStyle"
    >
      {{ $t('ads.preview') }}
    </div>
    <ins
      v-else
      :key="instance"
      ref="adElement"
      class="adsbygoogle"
      :style="adStyle"
      :data-ad-client="AD_CLIENT"
      :data-ad-slot="adSlot"
      :data-ad-format="adFormat"
      :data-full-width-responsive="fullWidthResponsive"
      :data-ad-layout="adLayout"
      :data-ad-layout-key="adLayoutKey"
    />
  </aside>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { AD_CLIENT, AD_PLACEMENTS, canShowAds, placementForSlot } from '@/config/ads';
import { adContext } from '@/utils/adBoundary';
import adsenseService from '@/services/adsenseService';

const props = defineProps({
  adSlot: { type: String, required: true },
  adFormat: { type: String, default: 'horizontal' },
  adLayout: { type: String, default: null },
  adLayoutKey: { type: String, default: null },
  fullWidthResponsive: { type: [Boolean, String], default: true },
  width: { type: [String, Number], default: null },
  height: { type: [String, Number], default: null },
  containerClass: { type: [String, Array, Object], default: '' },
  // Development-only layout preview. Never requests even a test Google ad.
  testMode: { type: Boolean, default: false },
});
const auth = useAuthStore();
const route = useRoute();
const placement = computed(() => placementForSlot(props.adSlot));
const preview = computed(() => import.meta.env.DEV && props.testMode);
const eligible = computed(() => canShowAds(adContext(auth, route))
  && AD_PLACEMENTS[placement.value]?.route === route.name
  && (preview.value || adsenseService.shouldShowAds()));
const adElement = ref(null);
const failed = ref(false);
const instance = ref(0);
const dimension = value => typeof value === 'number' ? `${value}px` : value;
const adStyle = computed(() => ({
  display: 'block', width: dimension(props.width) || '100%',
  height: dimension(props.height) || undefined,
}));
let generation = 0;
let cleanups = [];
function cleanup() {
  generation++;
  cleanups.forEach(stop => stop());
  cleanups = [];
}
function track(event) {
  if (preview.value) return;
  window.gtag?.('event', event, {
    ad_placement: placement.value,
    route_name: route.name,
    account_tier: auth.isAuthenticated ? 'basic' : 'visitor',
  });
}
function waitForWidth(element) {
  if (element.getBoundingClientRect().width > 0) return Promise.resolve(true);
  return new Promise(resolve => {
    let observer;
    const finish = value => { clearTimeout(timeout); observer?.disconnect(); resolve(value); };
    const timeout = setTimeout(() => finish(false), 4000);
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(() => {
        if (element.getBoundingClientRect().width > 0) finish(true);
      });
      observer.observe(element);
    }
    cleanups.push(() => finish(false));
  });
}
async function requestAd(run, loading) {
  const element = adElement.value;
  if (!element) return;
  const hasWidth = await waitForWidth(element);
  if (run !== generation || !eligible.value) return;
  const loaded = hasWidth && await loading;
  if (run !== generation || !eligible.value || !element.isConnected) return;
  if (!loaded) { failed.value = true; track('ad_slot_unavailable'); return; }
  let lastStatus;
  const observer = new MutationObserver(() => {
    const status = element.getAttribute('data-ad-status');
    if (status === lastStatus || !['filled', 'unfilled'].includes(status)) return;
    lastStatus = status;
    track(`ad_slot_${status}`);
    if (status === 'unfilled') failed.value = true;
  });
  observer.observe(element, { attributes: true, attributeFilter: ['data-ad-status'] });
  cleanups.push(() => observer.disconnect());
  if (adsenseService.pushAd(element)) track('ad_slot_requested');
  else { failed.value = true; track('ad_slot_unavailable'); }
}
watch([eligible, () => props.adSlot], async () => {
  cleanup();
  const run = generation;
  failed.value = false;
  instance.value++;
  if (!eligible.value || preview.value) return;
  await nextTick();
  if (run !== generation || !adElement.value) return;
  // Preserve Auto display revenue on eligible pages once useful content exists;
  // only the manual slot waits until it approaches the viewport.
  const loading = adsenseService.ensureLoaded();
  loading.then(loaded => {
    if (!loaded && run === generation) {
      failed.value = true;
      track('ad_slot_unavailable');
      cleanup();
    }
  });
  let requested = false;
  const requestOnce = () => {
    if (requested || run !== generation) return;
    requested = true;
    requestAd(run, loading);
  };
  if (typeof IntersectionObserver === 'undefined') { requestOnce(); return; }
  const observer = new IntersectionObserver(entries => {
    if (!entries.some(entry => entry.isIntersecting)) return;
    observer.disconnect();
    requestOnce();
  }, { rootMargin: '200px' });
  observer.observe(adElement.value);
  cleanups.push(() => observer.disconnect());
}, { immediate: true, flush: 'post' });
onBeforeUnmount(cleanup);
</script>

<style scoped>
.adsense-container {
  min-width: 0;
  width: 100%;
  margin: 24px 0;
  padding: 0 0 12px;
  border-block: 1px solid var(--growth-line, #e0e3d9);
  color: var(--growth-muted, #62665d);
}
.ad-caption { min-height: 44px; padding: 8px 0; font-size: 11px; }
.ad-caption > span { letter-spacing: .06em; text-transform: uppercase; }
.ad-caption a { display: inline-flex; align-items: center; min-height: 32px; color: inherit; text-decoration: underline; text-underline-offset: 3px; }
.ad-caption a:hover { color: var(--growth-ink, #20221f); }
.ad-caption a:focus-visible { outline: 2px solid var(--growth-ink, #20221f); outline-offset: 3px; }
.adsbygoogle, .ad-preview { min-height: 100px; max-width: 100%; }
.ad-preview { display: grid !important; place-items: center; background: var(--growth-paper, #f6f7f2); border: 1px dashed var(--growth-line, #e0e3d9); border-radius: 12px; font-size: 12px; }
@media (min-width: 768px) { .adsbygoogle, .ad-preview { min-height: 90px; } }
</style>
